import { assertNever } from '~/util';
import {
  bail,
  forEachPath,
  withExternalPath,
  withPath
} from '~/util/validation';
import { evaluateParametrized, isNum, isNumArr } from './exprEval';
import type { EvaluatedExpr } from './exprEval';
import type {
  Expression,
  Operation,
  PulserSequence,
  VariableRef,
  Waveform
} from './types';

type Parametrized = VariableRef | Expression;

function assertValid(condition: boolean, message: string): asserts condition {
  if (!condition) {
    bail(message);
  }
}

function isVariableRef(param: unknown): param is VariableRef {
  return (
    typeof param === 'object' &&
    param != null &&
    'variable' in param &&
    typeof (param as Record<string, unknown>).variable === 'string'
  );
}

function isExpression(param: unknown): param is Expression {
  return (
    typeof param === 'object' &&
    param != null &&
    'expression' in param &&
    typeof (param as Record<string, unknown>).expression === 'string'
  );
}

/**
 * Check if given value is a varialbe ref or expression,
 * feed that predicate into the typesystem.
 */
export function isParam(param: unknown): param is VariableRef | Expression {
  return isVariableRef(param) || isExpression(param);
}

/**
 * Validate raw imported pulser sequence data.
 *
 * Verifies if all internal data references are valid,
 * and all parametrized expressions evaluate to expected data type.
 */
export function validatePulserSequence(sequence: PulserSequence): void {
  function validateChannelName(name: string) {
    assertValid(
      Object.prototype.hasOwnProperty.call(sequence.channels, name),
      `Channel '${name}' is not defined`
    );
  }

  function assertEval(param: Parametrized): EvaluatedExpr {
    const evaluated = evaluateParametrized(param, sequence.variables);
    if (evaluated.err) {
      return withExternalPath(evaluated.val.path, () =>
        bail(evaluated.val.message)
      );
    }
    return evaluated.val;
  }

  function evaluateNum(param: Parametrized): number {
    const val = assertEval(param);
    assertValid(!val.array && isNum(val.value), 'Expected number');
    return val.value;
  }

  function evaluateInt(param: Parametrized): number {
    return Math.floor(evaluateNum(param));
  }

  function evaluateNumArray(param: Parametrized): number[] {
    const val = assertEval(param);
    assertValid(val.array && isNumArr(val.value), 'Expected number');
    return val.value;
  }

  function validateAtomParam(value: number | Parametrized): number {
    const atomValue = isParam(value) ? evaluateInt(value) : value;
    validateAtom(atomValue);
    return atomValue;
  }

  function validateNumParam(value: number | Parametrized): number {
    return isParam(value) ? evaluateNum(value) : value;
  }
  function validateNumArrayParam(value: number[] | Parametrized): number[] {
    return isParam(value) ? evaluateNumArray(value) : value;
  }

  function validateAtom(index: number) {
    assertValid(
      sequence.register.length > index,
      `Atom index '${index}' is out of range`
    );
  }

  function validateWaveform(waveform: Waveform) {
    switch (waveform.kind) {
      case 'constant':
        withPath(waveform, 'duration', (v) => validateNumParam(v));
        withPath(waveform, 'value', (v) => validateNumParam(v));
        break;
      case 'composite':
        withPath(waveform, 'waveforms', (v) =>
          forEachPath(v, validateWaveform)
        );
        break;
      case 'blackman':
        withPath(waveform, 'duration', validateNumParam);
        withPath(waveform, 'area', validateNumParam);
        break;
      case 'blackman_max':
        withPath(waveform, 'max_val', validateNumParam);
        withPath(waveform, 'area', validateNumParam);
        break;
      case 'ramp':
        withPath(waveform, 'duration', validateNumParam);
        withPath(waveform, 'start', validateNumParam);
        withPath(waveform, 'stop', validateNumParam);
        break;
      case 'kaiser':
        withPath(waveform, 'duration', validateNumParam);
        withPath(waveform, 'area', validateNumParam);
        withPath(waveform, 'beta', validateNumParam);
        break;
      case 'kaiser_max':
        withPath(waveform, 'max_val', validateNumParam);
        withPath(waveform, 'area', validateNumParam);
        withPath(waveform, 'beta', validateNumParam);
        break;
      case 'interpolated': {
        withPath(waveform, 'duration', validateNumParam);
        const vs = withPath(waveform, 'values', validateNumArrayParam);
        const ts = withPath(waveform, 'times', validateNumArrayParam);
        withPath(waveform, 'times', () => {
          assertValid(
            vs.length === ts.length,
            'Waveform interpolation values and times must have the same length'
          );
        });
        break;
      }
      case 'custom':
        withPath(waveform, 'samples', validateNumArrayParam);
        break;
      default:
        assertNever(waveform);
    }
  }

  const validateOperation = (op: Operation): void => {
    switch (op.op) {
      case 'align':
        withPath(op, 'channels', (channels) =>
          forEachPath(channels, validateChannelName)
        );
        break;
      case 'delay':
        withPath(op, 'channel', validateChannelName);
        break;
      case 'target':
        withPath(op, 'target', validateAtomParam);
        withPath(op, 'channel', validateChannelName);
        break;
      case 'pulse':
        withPath(op, 'amplitude', validateWaveform);
        withPath(op, 'detuning', validateWaveform);
        withPath(op, 'channel', validateChannelName);
        break;
      case 'phase_shift':
        withPath(op, 'phi', validateNumParam);
        withPath(op, 'targets', (targets) =>
          forEachPath(targets, validateAtomParam)
        );
        break;
      case 'enable_eom_mode':
      case 'add_eom_pulse':
      case 'disable_eom_mode':
      case 'modify_eom_setpoint':
        bail(
          `Operation '${op.op}' is not supported by the pulse viewer at the moment (EOM mode not yet supported)`
        );
        break;
      case 'pulse_arbitrary_phase':
        bail(
          `Operation '${op.op}' is not supported because arbitrary phase waveforms cannot be represented in the pulse viewer.`
        );
        break;
      case 'config_slm_mask':
        bail(
          `Operation '${op.op}' is not supported because SLM masks cannot be represented in the pulse viewer.`
        );
        break;
      case 'config_detuning_map':
      case 'add_dmm_detuning':
        bail(
          `Operation '${op.op}' is not supported because DMM / detuning-map operations cannot be represented in the pulse viewer.`
        );
        break;
      case 'truncate':
        bail(
          `Operation '${op.op}' is not supported because sequence truncation cannot be represented in the pulse viewer.`
        );
        break;
      default:
        assertNever(op);
    }
  };

  withPath(sequence, 'operations', (ops) =>
    forEachPath(ops, validateOperation)
  );
}
